import dayjs from "dayjs";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  message,
  Row,
  Spin,
  Typography,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as LocationIcon } from "assets/images/location-icon.svg";
import { ReactComponent as UserIcon } from "assets/images/user-icon.svg";
import { ReactComponent as CrownIcon } from "assets/images/crown-icon.svg";
import { ReactComponent as DownloadIcon } from "assets/images/download-icon.svg";
import { ReactComponent as DocumentIcon } from "assets/images/document-icon.svg";
import { ReactComponent as FlashIcon } from "assets/images/flash-icon.svg";
import FomoPromo from "assets/images/fomo-promo-image.png";
import { formatUSD, moneyFormat } from "helpers/money-format";
import "./Settings.scss";
import { InfluencerType } from "enums/InfluencerType";
import { useEffect, useMemo, useState } from "react";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import {
  BusinessState,
  getBusinessEscrow,
  getBusinessInfo,
  getBusinessSubscription,
} from "redux-context/business";
import { SubscriptionType } from "enums/SubscriptionType";
import { useNavigate } from "react-router-dom";
import { DownOutlined, EditOutlined, UpOutlined } from "@ant-design/icons";
import {
  getPaymentMethods,
  requestCancelSubscription,
  updatePaymentMethod,
} from "services/SauceService";
import { getBrandCardImage, hasBrandCardImage } from "helpers/brand-cards";
import { openNewTabSafe } from "helpers/url-helpers";


const DifferentPaymentMethodLocationsAlert: React.FC<{
  business: any;
  subscription: any;
}> = ({ business, subscription }) => {
  const MAX_VISIBLE_DIFFERENT_PAYMENT_LOCATIONS = 3;
  const [showAllLocations, setShowAllLocations] = useState<boolean>(false);
  const [customerPaymentMethodIds, setCustomerPaymentMethodIds] = useState<
    string[] | null
  >(null);

  useEffect(() => {
    if (!business?._id) return;
    if (!business.stripeCustomerId && !subscription?.stripeDetails) return;

    let cancelled = false;
    (async () => {
      try {
        const response: any = await getPaymentMethods({
          businessId: business._id,
        });
        if (cancelled) return;
        const ids = (response?.paymentMethods ?? []).map(
          (pm: { id: string }) => pm.id
        );
        setCustomerPaymentMethodIds(ids);
      } catch {
        if (!cancelled) setCustomerPaymentMethodIds(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [business?._id, business?.stripeCustomerId, subscription?.stripeDetails]);

  const locations = useMemo(() => {
    const venues = business?.venues;
    if (!Array.isArray(venues) || customerPaymentMethodIds === null) {
      return [];
    }
    const validIds = new Set(customerPaymentMethodIds);
    return venues.filter(
      (venue) =>
        venue.preferredPayment && !validIds.has(venue.preferredPayment)
    );
  }, [business?.venues, customerPaymentMethodIds]);

  const visibleLocations = useMemo(() => {
    if (showAllLocations) {
      return locations;
    }
    return locations.slice(0, MAX_VISIBLE_DIFFERENT_PAYMENT_LOCATIONS);
  }, [locations, showAllLocations]);

  useEffect(() => {
    setShowAllLocations(false);
  }, [locations.length]);

  if (locations.length === 0) {
    return null;
  }

  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16, borderRadius: 16 }}
      message="Some locations use a payment method that is not on file"
      description={
        <div>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            The card selected for the following location
            {locations.length > 1 ? "s" : ""} is no longer in your saved payment
            methods (for example, it may have been removed). Update each
            location under Settings → Locations → Payment to choose a valid
            card.
          </Typography.Paragraph>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            {visibleLocations.map((venue) => (
              <li key={venue.uuid || venue._id}>
                <strong>{venue.name || "Unnamed location"}</strong>
                {venue.addressDisplay ? (
                  <span style={{ color: "rgba(0,0,0,0.45)" }}>
                    {" "}
                    — {venue.addressDisplay}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          {locations.length > MAX_VISIBLE_DIFFERENT_PAYMENT_LOCATIONS ? (
            <span
              style={{
                display: "inline-block",
                marginTop: 10,
                fontWeight: 500,
                fontSize: 15,
                color: "#000",
                cursor: "pointer",
              }}
              onClick={() => setShowAllLocations((current) => !current)}
            >
              {showAllLocations ? (
                <>
                  Show less <UpOutlined style={{ fontSize: 12 }} />
                </>
              ) : (
                <>
                  Load more <DownOutlined style={{ fontSize: 12 }} />
                </>
              )}
            </span>
          ) : null}
        </div>
      }
    />
  );
};

const SubscriptionPage: React.FC = () => {
  const { subscription, loading, escrow, business } = useSelector(
    (state: BusinessState) => state.business
  );
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [showModalSubscription, setShowModalSubscription] =
    useState<boolean>(false);
  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;

  const nextPaymentDate = subscription?.stripeDetails?.nextPaymentDetails
    ? dayjs
      .utc(subscription?.stripeDetails?.nextPaymentDetails?.date)
      .format("MMM DD, YYYY")
    : "";

  const nextPaymentAmount = subscription?.stripeDetails?.nextPaymentDetails
    ? moneyFormat(
      subscription?.stripeDetails?.nextPaymentDetails?.amount,
      subscription?.stripeDetails?.nextPaymentDetails?.currency
    )
    : "";

  const paymentBrand = subscription?.stripeDetails?.defaultPaymentMethod?.brand;

  const paymentDetailCardInfo = hasBrandCardImage(paymentBrand)
    ? `∘∘∘∘ ${subscription?.stripeDetails?.defaultPaymentMethod?.last4}`
    : paymentBrand && subscription?.stripeDetails?.defaultPaymentMethod?.last4
      ? `${paymentBrand} ∘∘∘∘ ${subscription?.stripeDetails?.defaultPaymentMethod?.last4}`
      : "No payment method";

  const paymentDetailCardExp = subscription?.stripeDetails?.defaultPaymentMethod
    ?.expMonth
    ? `${subscription?.stripeDetails?.defaultPaymentMethod?.expMonth}/${subscription?.stripeDetails?.defaultPaymentMethod?.expYear}`
    : "";

  const basicRemaining =
    subscription?.spotsUsageByType?.find(
      (item) => item.type === InfluencerType.basic
    )?.amountRemaining || 0;

  const premiumRemaining =
    subscription?.spotsUsageByType?.find(
      (item) => item.type === InfluencerType.premium
    )?.amountRemaining || 0;

  const onClickDownloadInvoice = (invoice: { invoicePdf: string }) => {
    openNewTabSafe(invoice.invoicePdf);
  };

  const onClickCancelSubscription = () => {
    if (freemium) return;
    setShowModalSubscription(true);
  };

  const onCancelModal = () => {
    setShowModalSubscription(false);
  };

  const onConfirmCancelSubscription = async (
    cancelInfluencerBookings: boolean
  ) => {
    const params = {
      businessId: business._id,
      cancelInfluencerBookings,
    };

    const response = await requestCancelSubscription(params);
    if (response) {
      setShowModalSubscription(false);

      message.success("Subscription cancellation requested.");
      dispatch(getBusinessSubscription(true));
      dispatch(getBusinessEscrow(true));
    }
  };

  const onEditPaymentMethod = async () => {
    const params = {
      businessId: business._id,
    };

    const response: any = await updatePaymentMethod(params);
    if (response?.stripeSession?.url) {
      openNewTabSafe(response.stripeSession.url);
    } else {
      message.error("An error occurred while fetching the stripe session");
    }
  };

  useEffect(() => {
    dispatch(getBusinessInfo());
    dispatch(getBusinessSubscription(true));
    dispatch(getBusinessEscrow(true));
  }, [dispatch]);

  if (loading) return <Spin />;

  return (
    <div className="subscription">
      <CancelSubscriptionModal
        visible={showModalSubscription}
        onCancel={onCancelModal}
        onConfirm={onConfirmCancelSubscription}
      />
      <Typography.Title level={3}>Current Plan</Typography.Title>
      <Card loading={loading}>
        <Row justify="center" gutter={[12, 12]}>
          <Col style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 20 }}>
              <img
                src={FomoPromo}
                alt="Fomo promo"
                style={{ height: 100, width: 100 }}
              />
              <div>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {subscription?.planName}
                </Typography.Title>
                <Typography.Text>Current plan</Typography.Text>
                {subscription?.cancellationRequestDate ? (
                  <span
                    style={{
                      color: "#999",
                      fontSize: 14,
                      display: "block",
                      marginTop: 8,
                      width: 300,
                    }}
                  >
                    Cancelation has been requested on{" "}
                    {dayjs
                      .utc(subscription.cancellationRequestDate)
                      .format("MMMM D, YYYY h:mma")}
                    . Your subscription will not be renewed.
                  </span>
                ) : !freemium ? (
                  <span
                    style={{
                      color: "#999",
                      fontSize: 14,
                      display: "block",
                      marginTop: 8,
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                    onClick={onClickCancelSubscription}
                  >
                    Cancel Subscription
                  </span>
                ) : null}
              </div>
            </div>
          </Col>
          {subscription?.wildCardFoodiesEnabled ? (
            <Col xs={24} md={4}>
              <div className="box-info">
                <Typography.Text>
                  <UserIcon /> Total creators
                </Typography.Text>
                <Typography.Text
                  style={{ marginLeft: 16 }}
                >{`${subscription.totalRemaining} out of ${subscription?.totalAllowed}`}</Typography.Text>
              </div>
            </Col>
          ) : (
            <>
              <Col xs={24} md={4}>
                <div className="box-info">
                  <Typography.Text>
                    <UserIcon /> Basic creators
                  </Typography.Text>
                  <Typography.Text
                    style={{ marginLeft: 16 }}
                  >{`${basicRemaining} out of ${subscription?.basicAllowed}`}</Typography.Text>
                </div>
              </Col>
              <Col xs={24} md={4}>
                <div className="box-info">
                  <Typography.Text>
                    <CrownIcon /> Premium creators
                  </Typography.Text>
                  <Typography.Text
                    style={{ marginLeft: 16 }}
                  >{`${premiumRemaining} out of ${subscription?.premiumAllowed}`}</Typography.Text>
                </div>
              </Col>
            </>
          )}
          <Col xs={24} md={4}>
            <div className="box-info">
              <Typography.Text>
                <LocationIcon /> Locations
              </Typography.Text>
              <Typography.Text style={{ marginLeft: 16 }}>{`${subscription?.locationsUsage
                } out of ${subscription?.locationsAllowed || 0
                }`}</Typography.Text>
            </div>
          </Col>
          {freemium && (
            <Col xs={24} md={3}>
              <Button type="primary" onClick={() => navigate("/upgrade")}>
                <FlashIcon /> Upgrade
              </Button>
            </Col>
          )}
        </Row>
        {subscription?.stripeDetails?.nextPaymentDetails && (
          <>
            <Divider style={{ borderColor: "#FFAC39", borderWidth: 2 }} />
            <Row>
              <Col xs={12}>
                <Row style={{ gap: 48 }}>
                  <div className="box-info">
                    <span>Next payment date</span>
                    <Typography.Text>{nextPaymentDate}</Typography.Text>
                  </div>
                  <div className="box-info">
                    <span>Total</span>
                    <Typography.Text>
                      {nextPaymentAmount}
                      {subscription?.termFrequencyCharge
                        ? subscription.termFrequencyCharge === 1
                          ? "/mo"
                          : ` /${subscription.termFrequencyCharge} months`
                        : ""}
                    </Typography.Text>
                  </div>
                </Row>
              </Col>
            </Row>
          </>
        )}
      </Card>
      {(subscription?.stripeDetails || business.stripeCustomerId) && (
        <>
          <Typography.Title level={3}>Payment Methods</Typography.Title>
          <DifferentPaymentMethodLocationsAlert
            business={business}
            subscription={subscription}
          />
          <Card>
            <Row style={{ alignItems: "center" }}>
              <Typography.Text>
                {hasBrandCardImage(paymentBrand) && (
                  <img
                    src={getBrandCardImage(
                      subscription?.stripeDetails?.defaultPaymentMethod?.brand
                    )}
                    alt="Card brand"
                    style={{ marginRight: 10, height: 16 }}
                  />
                )}
                {paymentDetailCardInfo}
              </Typography.Text>
              <span className="payment-exp" style={{ margin: "0 20px" }}>
                {paymentDetailCardExp}
              </span>
              <Button icon={<EditOutlined />} onClick={onEditPaymentMethod} />
            </Row>
          </Card>

          {subscription?.stripeDetails?.invoices?.length > 0 && (
            <>
              <Typography.Title level={3}>Receipts</Typography.Title>
              <Row gutter={[8, 8]} style={{ height: 400, overflow: "auto" }}>
                {subscription?.stripeDetails?.invoices.map((invoice) => (
                  <Col xs={24}>
                    <Card key={invoice.id} className="card-invoice">
                      <Row>
                        <Col xs={6}>
                          <Typography.Text className="invoice-date">
                            <DocumentIcon />
                            {dayjs
                              .utc(invoice.date || "")
                              .format("MMM DD, YYYY")}
                          </Typography.Text>
                        </Col>
                        <Col xs={6}>
                          <Typography.Text>
                            {invoice?.description}
                          </Typography.Text>
                        </Col>
                        <Col xs={12} className="invoice-format">
                          <Typography.Text>
                            {moneyFormat(invoice.amount, invoice.currency)}{" "}
                            {invoice?.currency?.toUpperCase()}
                          </Typography.Text>
                          <DownloadIcon
                            onClick={() => onClickDownloadInvoice(invoice)}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {((escrow?.availableAmount || 0) > 0 ||
            (escrow?.reservedAmount || 0) > 0 ||
            (escrow?.statements?.length || 0) > 0 ||
            business.selfManagedCompensationEnabled) && (
              <>
                <Typography.Title level={3}>Escrow Ledger</Typography.Title>
                <Card>
                  <div style={{ display: "flex", gap: 20 }}>
                    <div className="box-info">
                      <span> Available Amount</span>
                      <Typography.Text>
                        {formatUSD(escrow.availableAmount || 0)}
                      </Typography.Text>
                    </div>

                    <div className="box-info">
                      <span> Reserved Amount</span>
                      <Typography.Text>
                        {formatUSD(escrow.reservedAmount || 0)}
                      </Typography.Text>
                    </div>
                  </div>

                  <Divider style={{ borderColor: "#FFAC39", borderWidth: 2 }} />

                  {(escrow?.statements?.length || 0) > 0 && (
                    <Row>
                      <Col xs={24}>
                        <Row gutter={[8, 8]}>
                          {escrow.statements?.map((statement) => (
                            <Col xs={24} key={statement._id}>
                              <div className="statement">
                                <Row>
                                  <Col xs={6}>
                                    <Typography.Text className="invoice-date">
                                      {dayjs
                                        .utc(statement.createdAt)
                                        .format("MMM DD, YYYY HH:mm")}
                                    </Typography.Text>
                                  </Col>
                                  <Col xs={6}>
                                    <Typography.Text>
                                      {statement.description}
                                    </Typography.Text>
                                  </Col>
                                  <Col xs={12} className="invoice-format">
                                    <Typography.Text>
                                      {moneyFormat(statement.amount, "USD")} USD
                                    </Typography.Text>
                                  </Col>
                                </Row>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </Col>
                    </Row>
                  )}
                </Card>
              </>
            )}
        </>
      )}
    </div>
  );
};

export default SubscriptionPage;
